import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  UserPlus, 
  Users, 
  Mail, 
  Copy, 
  Check,
  Clock,
  RefreshCw,
  Trash2,
  Send,
  Shield,
  Eye,
  Edit2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCESS_LEVELS = [
  { value: 'full', label: 'Full Access', description: 'Can approve transactions, add bonuses, reset PINs' },
  { value: 'limited', label: 'Limited', description: 'Can view and approve transactions only' },
  { value: 'view_only', label: 'View Only', description: 'Can only view child accounts' },
];

/**
 * Invite Card Component
 */
const InviteCard = ({ invite, onResend, onCancel }) => {
  const [copied, setCopied] = useState(false);
  
  const copyCode = () => {
    navigator.clipboard.writeText(invite.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Code copied!');
  };
  
  const isExpired = new Date(invite.expires_at) < new Date();
  
  return (
    <div 
      className={`p-4 rounded-xl border-2 transition-all ${
        invite.status === 'accepted' ? 'bg-green-50 border-green-200' :
        isExpired ? 'bg-slate-50 border-slate-200 opacity-60' :
        'bg-white border-slate-200'
      }`}
      data-testid={`invite-${invite.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            invite.invite_type === 'co_parent' ? 'bg-blue-100' : 'bg-purple-100'
          }`}>
            {invite.invite_type === 'co_parent' ? (
              <Users className="w-5 h-5 text-blue-600" />
            ) : (
              <UserPlus className="w-5 h-5 text-purple-600" />
            )}
          </div>
          <div>
            <p className="font-medium text-slate-800">
              {invite.invite_type === 'co_parent' ? 'Co-Parent Invite' : `Child: ${invite.child_username}`}
            </p>
            <p className="text-sm text-slate-500">{invite.email}</p>
          </div>
        </div>
        
        <Badge className={
          invite.status === 'accepted' ? 'bg-green-100 text-green-700' :
          isExpired ? 'bg-slate-100 text-slate-500' :
          'bg-amber-100 text-amber-700'
        }>
          {invite.status === 'accepted' ? 'Accepted' : isExpired ? 'Expired' : 'Pending'}
        </Badge>
      </div>
      
      {invite.status === 'pending' && !isExpired && (
        <div className="mt-4 flex items-center gap-2">
          <div className="flex-1 bg-slate-100 rounded-lg px-3 py-2 font-mono text-lg text-center">
            {invite.code}
          </div>
          <Button size="sm" variant="outline" onClick={copyCode}>
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onResend(invite.id)}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" className="text-red-500" onClick={() => onCancel(invite.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
      
      {invite.invite_type === 'co_parent' && (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Shield className="w-4 h-4" />
          <span>Access: {ACCESS_LEVELS.find(l => l.value === invite.access_level)?.label}</span>
        </div>
      )}
      
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
        <Clock className="w-3 h-3" />
        <span>
          {invite.status === 'accepted' ? 'Accepted' : 
           isExpired ? 'Expired' : 
           `Expires ${new Date(invite.expires_at).toLocaleDateString()}`}
        </span>
      </div>
    </div>
  );
};

/**
 * Co-Parent Manager Component
 */
const CoParentList = ({ token, coParents, onRefresh }) => {
  const handleUpdateAccess = async (coParentId, accessLevel) => {
    try {
      const response = await fetch(`${API_URL}/api/invites/co-parents/${coParentId}/access?access_level=${accessLevel}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Access level updated');
        onRefresh();
      } else {
        toast.error('Failed to update access');
      }
    } catch (error) {
      toast.error('Failed to update access');
    }
  };
  
  const handleRemove = async (coParentId) => {
    if (!confirm('Remove this co-parent? They will lose access to all children.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/invites/co-parents/${coParentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Co-parent removed');
        onRefresh();
      } else {
        toast.error('Failed to remove co-parent');
      }
    } catch (error) {
      toast.error('Failed to remove co-parent');
    }
  };
  
  if (coParents.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
        <p>No co-parents yet</p>
        <p className="text-sm">Invite a partner to help manage your children's accounts</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {coParents.map((cp) => (
        <div 
          key={cp.id}
          className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
          data-testid={`coparent-${cp.co_parent_id}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold">
                {cp.co_parent_username.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium text-slate-800">{cp.co_parent_username}</p>
              <p className="text-sm text-slate-500">Managing: {cp.customer_username}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Select
              value={cp.access_level}
              onValueChange={(value) => handleUpdateAccess(cp.co_parent_id, value)}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_LEVELS.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              size="sm"
              variant="ghost"
              className="text-red-500 hover:text-red-600"
              onClick={() => handleRemove(cp.co_parent_id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Main Invite Manager Component
 */
const InviteManager = ({ token }) => {
  const [invites, setInvites] = useState([]);
  const [coParents, setCoParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form state
  const [inviteType, setInviteType] = useState('co_parent');
  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState('full');
  const [childUsername, setChildUsername] = useState('');
  const [initialBalance, setInitialBalance] = useState('0');
  
  useEffect(() => {
    fetchData();
  }, [token]);
  
  const fetchData = async () => {
    try {
      const [invitesRes, coParentsRes] = await Promise.all([
        fetch(`${API_URL}/api/invites`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/invites/co-parents`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (invitesRes.ok) setInvites(await invitesRes.json());
      if (coParentsRes.ok) setCoParents(await coParentsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateInvite = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (inviteType === 'child' && !childUsername) {
      toast.error('Please enter a username for the child');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/invites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email,
          invite_type: inviteType,
          access_level: inviteType === 'co_parent' ? accessLevel : undefined,
          child_username: inviteType === 'child' ? childUsername : undefined,
          initial_balance: inviteType === 'child' ? parseFloat(initialBalance) || 0 : undefined
        })
      });
      
      if (response.ok) {
        toast.success('Invite sent!');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create invite');
      }
    } catch (error) {
      toast.error('Failed to create invite');
    }
  };
  
  const handleResend = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/invites/${inviteId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Invite resent!');
        fetchData();
      } else {
        toast.error('Failed to resend invite');
      }
    } catch (error) {
      toast.error('Failed to resend invite');
    }
  };
  
  const handleCancel = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/invites/${inviteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Invite cancelled');
        fetchData();
      } else {
        toast.error('Failed to cancel invite');
      }
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };
  
  const resetForm = () => {
    setEmail('');
    setAccessLevel('full');
    setChildUsername('');
    setInitialBalance('0');
    setInviteType('co_parent');
  };
  
  const pendingInvites = invites.filter(i => i.status === 'pending');
  
  return (
    <Card data-testid="invite-manager">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-500" />
            Invites & Co-Parents
          </CardTitle>
          <CardDescription>
            Invite co-parents or create accounts for children via email
          </CardDescription>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-invite-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              New Invite
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Invite</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div>
                <Label>Invite Type</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    type="button"
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      inviteType === 'co_parent' ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                    }`}
                    onClick={() => setInviteType('co_parent')}
                  >
                    <Users className="w-5 h-5 text-blue-600 mb-1" />
                    <p className="font-medium">Co-Parent</p>
                    <p className="text-xs text-slate-500">Share management</p>
                  </button>
                  <button
                    type="button"
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      inviteType === 'child' ? 'border-purple-500 bg-purple-50' : 'border-slate-200'
                    }`}
                    onClick={() => setInviteType('child')}
                  >
                    <UserPlus className="w-5 h-5 text-purple-600 mb-1" />
                    <p className="font-medium">Child</p>
                    <p className="text-xs text-slate-500">Create new account</p>
                  </button>
                </div>
              </div>
              
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  data-testid="invite-email"
                />
              </div>
              
              {inviteType === 'co_parent' && (
                <div>
                  <Label>Access Level</Label>
                  <Select value={accessLevel} onValueChange={setAccessLevel}>
                    <SelectTrigger data-testid="invite-access-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCESS_LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          <div>
                            <p className="font-medium">{level.label}</p>
                            <p className="text-xs text-slate-500">{level.description}</p>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {inviteType === 'child' && (
                <>
                  <div>
                    <Label>Child's Username</Label>
                    <Input
                      value={childUsername}
                      onChange={(e) => setChildUsername(e.target.value)}
                      placeholder="e.g., tommy123"
                      data-testid="invite-child-username"
                    />
                  </div>
                  <div>
                    <Label>Initial Balance ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={initialBalance}
                      onChange={(e) => setInitialBalance(e.target.value)}
                      data-testid="invite-initial-balance"
                    />
                  </div>
                </>
              )}
              
              <Button onClick={handleCreateInvite} className="w-full" data-testid="send-invite-btn">
                <Send className="w-4 h-4 mr-2" />
                Send Invite
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Co-Parents Section */}
        <div>
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Co-Parents ({coParents.length})
          </h3>
          <CoParentList token={token} coParents={coParents} onRefresh={fetchData} />
        </div>
        
        {/* Pending Invites Section */}
        {pendingInvites.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending Invites ({pendingInvites.length})
            </h3>
            <div className="space-y-3">
              {pendingInvites.map((invite) => (
                <InviteCard
                  key={invite.id}
                  invite={invite}
                  onResend={handleResend}
                  onCancel={handleCancel}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InviteManager;
